// The chat's Markdown, as Discord's: what a message's text means, as a tree the page draws
// (ui/Markdown.tsx) — never HTML, so nothing written in a message becomes markup. Blocks: code (```),
// quotes (`> `, and `>>> ` for the rest of the message), headings (`# `, `## `, `### `), lists (`- `, `* `,
// `1. `) and lines of text. In a line: `code`, **bold**, *italic* and _italic_, __underlined__, ~~struck~~,
// ||hidden|| till clicked, links — written out (https://…, <https://…>) or named ([text](https://…)) — and
// users named (<@number>, the server's form of a mention). A backslash takes the next mark as it is.

export type Inline =
  | { t: "text"; v: string }
  | { t: "br" }
  | { t: "code"; v: string }
  | { t: "b" | "i" | "u" | "s" | "spoiler"; c: Inline[] }
  | { t: "link"; href: string; c: Inline[] }
  | { t: "mention"; user: number };

export type Block =
  | { t: "p"; c: Inline[] }
  | { t: "code"; lang: string; v: string }
  | { t: "quote"; c: Block[] }
  | { t: "h"; level: number; c: Inline[] }
  | { t: "list"; ordered: boolean; start: number; items: Inline[][] };

/** The marks a backslash takes as they are. */
const ESCAPED = "\\*_~|`[]()<>#>-.!:@";
/** The marks of spans, longest first: a double one is not two single ones. */
const SPANS: [string, "b" | "i" | "u" | "s" | "spoiler"][] = [
  ["||", "spoiler"],
  ["**", "b"],
  ["__", "u"],
  ["~~", "s"],
  ["*", "i"],
  ["_", "i"],
];
/** How deep spans nest at most: past it, marks are text. */
const DEEPEST = 6;

/** The blocks of text `text`. */
export function parse(text: string): Block[] {
  return blocks(text.replace(/\r\n?/g, "\n").split("\n"), true);
}

function blocks(lines: string[], quotes: boolean): Block[] {
  const out: Block[] = [];
  let para: string[] = [];
  const flush = () => {
    // A paragraph without its empty lines at the ends; those between stay.
    while (para.length > 0 && para[0].trim() === "") para.shift();
    while (para.length > 0 && para[para.length - 1].trim() === "") para.pop();
    if (para.length > 0) out.push({ t: "p", c: inline(para.join("\n")) });
    para = [];
  };
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const fence = codeBlock(lines, i);
    if (fence !== null) {
      flush();
      out.push(fence[0]);
      i = fence[1];
      continue;
    }
    if (quotes && line.startsWith(">>> ")) {
      flush();
      out.push({ t: "quote", c: blocks([line.slice(4), ...lines.slice(i + 1)], false) });
      break;
    }
    if (quotes && /^> ?/.test(line) && !line.startsWith(">>>")) {
      flush();
      const inner: string[] = [];
      while (i < lines.length && /^> ?/.test(lines[i]) && !lines[i].startsWith(">>>")) {
        inner.push(lines[i].replace(/^> ?/, ""));
        i++;
      }
      out.push({ t: "quote", c: blocks(inner, false) });
      continue;
    }
    const heading = /^(#{1,3}) (.*\S.*)$/.exec(line);
    if (heading !== null) {
      flush();
      out.push({ t: "h", level: heading[1].length, c: inline(heading[2].trim()) });
      i++;
      continue;
    }
    const item = listItem(line);
    if (item !== null) {
      flush();
      const items: Inline[][] = [];
      while (i < lines.length) {
        const next = listItem(lines[i]);
        if (next === null || next.ordered !== item.ordered) break;
        items.push(inline(next.text));
        i++;
      }
      out.push({ t: "list", ordered: item.ordered, start: item.start, items });
      continue;
    }
    para.push(line);
    i++;
  }
  flush();
  return out;
}

/** The block of code that starts at line `i`, and the line after it; `null` if none starts there: a
 * line of ``` (and the code's language) opens it, a line that ends with ``` closes it — on the same line,
 * code of one line. */
function codeBlock(lines: string[], i: number): [Block, number] | null {
  const line = lines[i];
  if (!line.startsWith("```")) return null;
  const rest = line.slice(3);
  const end = rest.indexOf("```");
  if (end >= 0) {
    const v = rest.slice(0, end);
    return v.trim() === "" ? null : [{ t: "code", lang: "", v: v.trim() }, i + 1];
  }
  const lang = /^[\w+#.-]{1,20}$/.test(rest.trim()) ? rest.trim() : "";
  const body: string[] = lang === "" && rest.trim() !== "" ? [rest] : [];
  for (let k = i + 1; k < lines.length; k++) {
    const l = lines[k];
    const close = l.lastIndexOf("```");
    if (close >= 0 && l.trimEnd().endsWith("```")) {
      if (l.slice(0, close).trim() !== "") body.push(l.slice(0, close));
      return [{ t: "code", lang, v: body.join("\n") }, k + 1];
    }
    body.push(l);
  }
  return null;
}

/** A line of a list: whether numbered, its number, and its text; `null` if the line is none. */
function listItem(line: string): { ordered: boolean; start: number; text: string } | null {
  const bullet = /^\s{0,3}[-*] +(\S.*)$/.exec(line);
  if (bullet !== null) return { ordered: false, start: 1, text: bullet[1] };
  const number = /^\s{0,3}(\d{1,9})[.)] +(\S.*)$/.exec(line);
  if (number !== null) return { ordered: true, start: Number(number[1]), text: number[2] };
  return null;
}

/** The spans of a line (or lines: a line break is `br`). */
export function inline(text: string): Inline[] {
  return spans(text, 0);
}

function spans(s: string, depth: number): Inline[] {
  const out: Inline[] = [];
  let text = "";
  const flush = () => {
    if (text === "") return;
    text.split("\n").forEach((part, k) => {
      if (k > 0) out.push({ t: "br" });
      if (part !== "") out.push({ t: "text", v: part });
    });
    text = "";
  };
  let i = 0;
  while (i < s.length) {
    const ch = s[i];
    if (ch === "\\" && i + 1 < s.length && ESCAPED.includes(s[i + 1])) {
      text += s[i + 1];
      i += 2;
      continue;
    }
    if (ch === "`") {
      const run = s.startsWith("``", i) ? 2 : 1;
      const close = s.indexOf("`".repeat(run), i + run);
      if (close > i + run && s.slice(i + run, close).trim() !== "") {
        flush();
        out.push({ t: "code", v: s.slice(i + run, close) });
        i = close + run;
        continue;
      }
      text += s.slice(i, i + run);
      i += run;
      continue;
    }
    if (ch === "<") {
      const mention = /^<@(\d{1,16})>/.exec(s.slice(i, i + 20));
      if (mention !== null) {
        flush();
        out.push({ t: "mention", user: Number(mention[1]) });
        i += mention[0].length;
        continue;
      }
      const bare = /^<(https?:\/\/[^\s<>]+)>/.exec(s.slice(i, i + 2100));
      if (bare !== null) {
        flush();
        out.push({ t: "link", href: bare[1], c: [{ t: "text", v: bare[1] }] });
        i += bare[0].length;
        continue;
      }
    }
    if (ch === "[") {
      const named = /^\[([^\]\n]{1,200})\]\((https?:\/\/[^\s()<>]+)\)/.exec(s.slice(i, i + 2400));
      if (named !== null) {
        flush();
        out.push({ t: "link", href: named[2], c: depth < DEEPEST ? spans(named[1], depth + 1) : [{ t: "text", v: named[1] }] });
        i += named[0].length;
        continue;
      }
    }
    if ((ch === "h" || ch === "H") && /^https?:\/\//i.test(s.slice(i, i + 8)) && (i === 0 || !isWord(s[i - 1]))) {
      const url = linkAt(s, i);
      if (url !== "") {
        flush();
        out.push({ t: "link", href: url, c: [{ t: "text", v: url }] });
        i += url.length;
        continue;
      }
    }
    const span = depth < DEEPEST ? spanAt(s, i) : null;
    if (span !== null) {
      flush();
      out.push({ t: span.kind, c: spans(span.inner, depth + 1) });
      i = span.end;
      continue;
    }
    text += ch;
    i++;
  }
  flush();
  return out;
}

/** The link written out at `i` of `s`: up to a space, without the marks after it that end a sentence
 * rather than the link (and a closing bracket that does not close one of its own). */
function linkAt(s: string, i: number): string {
  let j = i;
  while (j < s.length && !/[\s<>]/.test(s[j])) j++;
  let url = s.slice(i, j);
  for (;;) {
    const last = url[url.length - 1];
    if (".,!?:;'\"*_~|".includes(last)) url = url.slice(0, -1);
    else if (last === ")" && count(url, "(") < count(url, ")")) url = url.slice(0, -1);
    else break;
  }
  return /^https?:\/\/[^/\s]/.test(url) ? url : "";
}

function count(s: string, ch: string): number {
  let n = 0;
  for (const c of s) if (c === ch) n++;
  return n;
}

/** Whether `ch` is a letter or a digit: `_` inside a word is no mark (snake_case). */
function isWord(ch: string): boolean {
  return /[\p{L}\p{N}]/u.test(ch);
}

/** The span whose mark starts at `i` of `s`: its kind, what it holds and where it ends; `null` if a mark
 * there opens none — it has no closing one, or holds nothing. */
function spanAt(s: string, i: number): { kind: "b" | "i" | "u" | "s" | "spoiler"; inner: string; end: number } | null {
  for (const [mark, kind] of SPANS) {
    if (!s.startsWith(mark, i)) continue;
    const single = mark.length === 1;
    // `_` and `__` open only where a word does not go on, and close only where it does not go on after.
    const wordy = mark[0] === "_";
    if (wordy && i > 0 && isWord(s[i - 1])) continue;
    let from = i + mark.length;
    while (from < s.length) {
      const close = s.indexOf(mark, from);
      if (close < 0) break;
      const escaped = s[close - 1] === "\\";
      // A single mark is not half of a double one: `*a **b** c*`.
      const half = single && (s[close + 1] === mark || (s[close - 1] === mark && close - 1 > i));
      const inner = s.slice(i + mark.length, close);
      const tight = !single || (inner.trim() === inner && inner !== "");
      const wordAfter = wordy && close + mark.length < s.length && isWord(s[close + mark.length]);
      if (!escaped && !half && tight && !wordAfter && inner.trim() !== "")
        return { kind, inner, end: close + mark.length };
      from = close + 1;
    }
    // A mark that closes nothing may still open a shorter one (`**` then `*`): go on.
  }
  return null;
}

/** The users text `text` names, each once, in order. */
export function mentions(text: string): number[] {
  const out: number[] = [];
  for (const m of text.matchAll(/<@(\d{1,16})>/g)) {
    const u = Number(m[1]);
    if (!out.includes(u)) out.push(u);
  }
  return out;
}

/** One emoji — its sequences of several, flags and keycaps too. */
const EMOJI =
  /^(?:\p{Regional_Indicator}{2}|[0-9#*]️?⃣|\p{Extended_Pictographic}(?:️|\p{Emoji_Modifier})?(?:‍\p{Extended_Pictographic}(?:️|\p{Emoji_Modifier})?)*)$/u;

/** How many emoji text `text` is, if it is nothing but emoji (and spaces) — at most `most` of them; 0 if
 * it is anything else. A message of a few emoji shows them large. */
export function emojiOnly(text: string, most = 27): number {
  const t = text.trim();
  if (t === "" || t.length > most * 12) return 0;
  const segments = [...new Intl.Segmenter("ru", { granularity: "grapheme" }).segment(t)]
    .map((s) => s.segment)
    .filter((s) => s.trim() !== "");
  if (segments.length > most) return 0;
  return segments.every((s) => EMOJI.test(s)) ? segments.length : 0;
}
